import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Campaign, Demographics, Screen } from '../data';
import { CampaignCreative, EntityTotals, HourlyAveragePoint } from '../adapters/campaigns';
import { fmtFull } from '../utils/formatters';
import { getAllScreens } from '../adapters/campaigns';
import { StaticNetworkMap as NetworkMap } from './StaticNetworkMap';

// Same brand theme as CreativeReport.tsx (creative-level export), kept local
// since neither file exports it — duplicating a handful of style constants
// here keeps the two report exports decoupled and independently editable.
const THEME = {
  sheetBg: '#F7F8FA',
  panelBg: '#ffffff',
  panelBorder: '1px solid #E6E9ED',
  panelShadow: '0 1px 2px rgba(15,23,42,0.03)',
  trackBg: '#EEF1F3',
  green: '#4ADE80',
  greenDeep: '#0a6e57',
  greenDark: '#00783A',
  navy: '#0D361F',
  textStrong: '#0B1220',
  text: '#1e293b',
  textMuted: '#64748b',
  textFaint: '#94a3b8',
};

interface PopReportProps {
  campaign: Campaign;
  startDate: string;
  endDate: string;
  totals: { plays: number; impressions: number };
  hourlyAverage: HourlyAveragePoint[];
  demographics: Demographics | null;
  screens: Screen[];
  creatives: CampaignCreative[];
  creativeCounts: EntityTotals;
  onClose: () => void;
}

function fmtDate(s: string, includeYear = true): string {
  if (!s) return '';
  return new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: includeYear ? 'numeric' : undefined });
}

// "Aug 5 - Aug 11, 2026" when both ends share a year (dropping the
// redundant year off the start), "Dec 28, 2025 - Jan 3, 2026" when they
// don't, or "Aug 5, 2026 - present" for an open-ended flight.
function fmtDateRange(startDate: string, endDate: string): string {
  if (!startDate) return '';
  if (!endDate) return `${fmtDate(startDate)} - present`;
  const sameYear = new Date(startDate + 'T00:00:00').getFullYear() === new Date(endDate + 'T00:00:00').getFullYear();
  return `${fmtDate(startDate, !sameYear)} - ${fmtDate(endDate)}`;
}

function isImageCreative(c: { type?: string; creativeUrl?: string }): boolean {
  if (c.type) return /image/i.test(c.type);
  return !!c.creativeUrl && /\.(png|jpe?g|webp|gif)(\?|$)/i.test(c.creativeUrl);
}

// Sizing for the side-by-side creative thumbnails shown when a campaign has
// more than one image creative.
const THUMB_W = 70;
const THUMB_H = 124; // 70 * 16/9
const THUMB_GAP = 4;

function hourLabel12(h: number): string {
  const period = h < 12 ? 'a' : 'p';
  const hr12 = h % 12 === 0 ? 12 : h % 12;
  return `${hr12}${period}`;
}

function hourLabelFull(h: number): string {
  const period = h < 12 ? 'AM' : 'PM';
  const hr12 = h % 12 === 0 ? 12 : h % 12;
  return `${hr12} ${period}`;
}

function groupByVenue(screens: Screen[]): { key: string; plays: number; impressions: number }[] {
  const map = new Map<string, { key: string; plays: number; impressions: number }>();
  for (const s of screens) {
    const key = (s.venue && s.venue.trim()) || s.name;
    const existing = map.get(key) ?? { key, plays: 0, impressions: 0 };
    existing.plays += s.plays ?? 0;
    existing.impressions += s.impressions ?? 0;
    map.set(key, existing);
  }
  return Array.from(map.values());
}

// Hour-of-day bar chart — dark bars with the single peak hour highlighted in
// brand green and its value called out above it, matching the "Average
// Delivery, Hour by Hour" chart in the client-facing template. Plots the
// true per-day average impressions for each hour (from the backend's hourly
// average endpoint).
function HourlyBarChart({ data }: { data: { hour: number; impressions: number }[] }) {
  // Sized to match the chart panel's actual rendered box (roughly 424x252 at
  // the report's 720px width — chart column width minus the map column and
  // panel padding, by the row's ~280px height minus panel padding) so
  // preserveAspectRatio="none" below scales uniformly instead of squashing
  // the chart horizontally while stretching it vertically.
  const W = 424, H = 252;
  const pad = { top: 34, right: 8, bottom: 22, left: 8 };
  const innerW = W - pad.left - pad.right;
  const innerH = H - pad.top - pad.bottom;
  const n = data.length;
  const gap = 3;
  const barW = (innerW - gap * (n - 1)) / Math.max(n, 1);
  const maxV = Math.max(1, ...data.map(d => d.impressions)) * 1.12;
  const x = (i: number) => pad.left + i * (barW + gap);
  const barH = (v: number) => (v / maxV) * innerH;

  const peakIdx = data.reduce((best, d, i) => (d.impressions > data[best].impressions ? i : best), 0);
  const peak = data[peakIdx];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: '100%', display: 'block' }}>
      <line x1={pad.left} y1={pad.top + innerH} x2={W - pad.right} y2={pad.top + innerH} stroke="#E9ECEF" strokeWidth={1} />
      {data.map((d, i) => {
        const h = Math.max(barH(d.impressions), 2);
        const isPeak = i === peakIdx && d.impressions > 0;
        return (
          <rect
            key={i}
            x={x(i)}
            y={pad.top + innerH - h}
            width={barW}
            height={h}
            rx={1.5}
            fill={isPeak ? THEME.green : THEME.navy}
          />
        );
      })}
      {peak && peak.impressions > 0 && (
        <g>
          <rect x={x(peakIdx) + barW / 2 - 22} y={pad.top - 22} width={44} height={17} rx={5} fill={THEME.green} />
          <text x={x(peakIdx) + barW / 2} y={pad.top - 10} fontSize="10" fontWeight={800} fill="#fff" textAnchor="middle">
            {fmtFull(Math.round(peak.impressions))}
          </text>
        </g>
      )}
      {data.map((d, i) => (
        <text key={i} x={x(i) + barW / 2} y={H - 4} fontSize="7" fill={THEME.textFaint} textAnchor="middle">
          {hourLabel12(d.hour)}
        </text>
      ))}
    </svg>
  );
}

export function PopReport({
  campaign, startDate, endDate, totals, hourlyAverage, demographics,
  screens, creatives, creativeCounts, onClose,
}: PopReportProps) {
  const creativeRows = useMemo(() => {
    return creatives
      .map(c => ({ ...c, plays: creativeCounts[c.id]?.plays ?? 0, impressions: creativeCounts[c.id]?.impressions ?? 0 }))
      .sort((a, b) => b.impressions - a.impressions);
  }, [creatives, creativeCounts]);

  // Distinct venues/addresses — used only for the "N screens across M
  // locations" counts; the ranked list below is per-screen.
  const venues = useMemo(() => groupByVenue(screens), [screens]);
  const screensByImpressions = useMemo(() => [...screens].sort((a, b) => (b.impressions ?? 0) - (a.impressions ?? 0)), [screens]);
  const topScreens = screensByImpressions.slice(0, 5);
  const maxScreenImpressions = Math.max(...topScreens.map(s => s.impressions ?? 0), 1);

  const [mapFailed, setMapFailed] = useState(false);
  const campaignPins = useMemo(() => screens.filter(s => s.lat && s.lon), [screens]);
  const showMap = !mapFailed && campaignPins.length > 0;

  // "Network" tab: every screen in our inventory, fetched on first open.
  const [tab, setTab] = useState<'report' | 'network'>('report');
  const [inventory, setInventory] = useState<Screen[] | null>(null);
  const [inventoryError, setInventoryError] = useState(false);
  const [networkMapFailed, setNetworkMapFailed] = useState(false);
  useEffect(() => {
    if (tab !== 'network' || inventory) return;
    let cancelled = false;
    setInventoryError(false);
    getAllScreens()
      .then(r => { if (!cancelled) setInventory(r); })
      .catch(() => { if (!cancelled) setInventoryError(true); });
    return () => { cancelled = true; };
  }, [tab, inventory]);
  const inventoryPins = useMemo(() => (inventory ?? []).filter(s => s.lat && s.lon), [inventory]);
  const inventoryLocations = useMemo(() => groupByVenue(inventory ?? []).length, [inventory]);
  const inventoryNeighborhoods = useMemo(
    () => new Set((inventory ?? []).map(s => s.neighborhood).filter(Boolean)).size,
    [inventory]
  );

  const uniqueLocations = venues.length;
  const avgImprPerPlay = totals.plays ? totals.impressions / totals.plays : null;

  // Top 3 image creatives by impressions — shown as a cascading stack (top
  // performer fully in front, each next one offset enough to be clearly its
  // own image, not just a sliver) so the corner signals "there's more than
  // one creative running," not just a single hero shot.
  const topImageCreatives = useMemo(
    () => creativeRows.filter(c => c.creativeUrl && isImageCreative(c)).slice(0, 3),
    [creativeRows]
  );
  const stackImageUrls = topImageCreatives.length
    ? topImageCreatives.map(c => c.creativeUrl!)
    : (campaign.imageUrl ? [campaign.imageUrl] : []);
  const heroImageUrl = stackImageUrls[0];

  // True per-day average impressions by hour, from the backend's hourly
  // average endpoint (sum/day_count) — bucketed into a canonical 24-slot
  // array keyed by hour_of_day, since the backend can skip hours with zero
  // delivery (overnight, etc.) and the bar chart below assumes 24
  // evenly-spaced, correctly-labeled hours.
  const hourlyData = useMemo(() => {
    const buckets = Array.from({ length: 24 }, () => ({ impressions: 0, plays: 0 }));
    for (const pt of hourlyAverage) {
      const h = pt.hour_of_day;
      if (h < 0 || h > 23) continue;
      buckets[h].impressions = pt.average_impressions ?? 0;
      buckets[h].plays = pt.average_plays ?? 0;
    }
    return buckets.map((b, hour) => ({ hour, impressions: b.impressions, plays: b.plays }));
  }, [hourlyAverage]);
  const peakHour = useMemo(() => {
    const withData = hourlyData.filter(h => h.impressions > 0);
    return withData.length ? withData.reduce((a, b) => (b.impressions > a.impressions ? b : a)) : null;
  }, [hourlyData]);
  // Sums across the average-per-day buckets (not the flight's real totals,
  // which live on a different scale).
  const sumHourlyAverageImpressions = hourlyData.reduce((a, h) => a + h.impressions, 0);
  const overallHourlyAverage = sumHourlyAverageImpressions / 24;

  // True per-day average impressions, spread evenly across every screen
  // running the flight — a simple per-screen productivity figure built from
  // the same per-day average basis as the hourly chart above.
  const avgImpressionsPerScreenPerDay = screens.length ? sumHourlyAverageImpressions / screens.length : null;

  const leadGender = demographics?.gender.length ? [...demographics.gender].sort((a, b) => b.share - a.share)[0] : null;
  const leadAge = demographics?.age.length ? [...demographics.age].sort((a, b) => b.share - a.share)[0] : null;
  const maxAgeShare = demographics?.age.length ? Math.max(...demographics.age.map(a => a.share)) : 1;

  const flight = fmtDateRange(startDate, endDate) || campaign.flight;
  const subtitle = [
    flight || null,
    screens.length ? `${fmtFull(screens.length)} screen${screens.length === 1 ? '' : 's'}` : null,
    uniqueLocations ? `${fmtFull(uniqueLocations)} location${uniqueLocations === 1 ? '' : 's'}` : null,
  ].filter(Boolean).join(' • ');

  const sheetRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);

  // Standard US Letter width, portrait — height follows the content so the
  // image always fills the entire page, edge to edge, no margin.
  const PAGE_W_IN = 8.5;

  const slug = (s: string) => s.trim().replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '') || 'report';

  const handleExport = async () => {
    const el = sheetRef.current;
    if (!el || exporting) return;
    setExporting(true);
    // The rounded corners + drop shadow are a screen-only "card on a modal"
    // affordance — strip them for the capture so the page is pure content,
    // then restore for the on-screen preview.
    const prevRadius = el.style.borderRadius;
    const prevShadow = el.style.boxShadow;
    el.style.borderRadius = '0';
    el.style.boxShadow = 'none';
    try {
      const canvas = await html2canvas(el, {
        scale: 2,
        backgroundColor: THEME.sheetBg,
        useCORS: true,
      });
      const img = canvas.toDataURL('image/png');

      const imgRatio = canvas.width / canvas.height;
      const drawW = PAGE_W_IN;
      const drawH = drawW / imgRatio;

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'in', format: [PAGE_W_IN, drawH] });
      pdf.addImage(img, 'PNG', 0, 0, drawW, drawH, undefined, 'FAST');

      const filename = `${slug(campaign.client?.name || 'client')}_${slug(campaign.name)}${tab === 'network' ? '_Network-Map' : '_PoP-Report'}.pdf`;
      pdf.save(filename);
    } finally {
      el.style.borderRadius = prevRadius;
      el.style.boxShadow = prevShadow;
      setExporting(false);
    }
  };

  const content = (
      <div id="pop-report-overlay" style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(3,8,14,0.7)', overflowY: 'auto', padding: '32px 16px' }}>
        <div style={{ maxWidth: 720, margin: '0 auto 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'flex', gap: 4, padding: 3, borderRadius: 10, background: 'rgba(255,255,255,0.08)' }}>
            {([['report', 'Report'], ['network', 'Network Map']] as const).map(([key, label]) => (
              <button key={key} onClick={() => setTab(key)} style={{
                padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700,
                background: tab === key ? '#fff' : 'transparent', color: tab === key ? THEME.textStrong : 'rgba(255,255,255,0.75)',
              }}>{label}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            Close
          </button>
          <button onClick={handleExport} disabled={exporting} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: 'var(--brand-green-500)', color: 'var(--brand-navy-900)', fontSize: 12.5, fontWeight: 800, cursor: exporting ? 'default' : 'pointer', fontFamily: 'inherit', opacity: exporting ? 0.7 : 1 }}>
            {exporting ? 'Generating…' : 'Download PDF'}
          </button>
          </div>
        </div>

        {tab === 'network' ? (
          <div ref={sheetRef} id="pop-report-sheet" style={{ width: 720, maxWidth: '100%', margin: '0 auto', background: THEME.sheetBg, borderRadius: 16, boxShadow: '0 30px 80px rgba(0,0,0,0.55)', padding: 26, display: 'flex', flexDirection: 'column', gap: 20, fontSize: 12, color: THEME.text }}>
            <div>
              <img src="/logo-light.svg" alt="Smartstreet" style={{ height: 46 }} />
              <div style={{ height: 1, background: '#E5E9EC', marginTop: 16 }} />
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: THEME.textMuted, marginBottom: 6 }}>Network</div>
              <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.01em', color: THEME.textStrong }}>Smartstreet Screen Locations</div>
            </div>
            {inventory && (
              <div style={{ display: 'flex', alignItems: 'stretch', gap: 16 }}>
                {[
                  { value: fmtFull(inventory.length), label: 'Screens' },
                  { value: fmtFull(inventoryLocations), label: 'Locations' },
                  ...(inventoryNeighborhoods ? [{ value: fmtFull(inventoryNeighborhoods), label: 'Neighborhoods' }] : []),
                ].map((st, i) => (
                  <React.Fragment key={st.label}>
                    {i > 0 && <div style={{ width: 1, background: '#E6E9ED', alignSelf: 'stretch' }} />}
                    <Stat value={st.value} label={st.label} />
                  </React.Fragment>
                ))}
              </div>
            )}
            {inventoryPins.length > 0 && !networkMapFailed ? (
              <NetworkMap
                screens={inventoryPins}
                onFail={() => setNetworkMapFailed(true)}
                style={{ border: THEME.panelBorder }}
                noteStyle={{ color: THEME.textMuted }}
              />
            ) : (
              <div style={{ width: '100%', height: 360, borderRadius: 12, border: '1px dashed #C7CDD4', background: THEME.trackBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: THEME.textFaint }}>
                {inventoryError ? 'Couldn’t load screen locations.' : networkMapFailed || (inventory && !inventoryPins.length) ? 'Screen locations unavailable' : 'Loading screen locations…'}
              </div>
            )}
          </div>
        ) : (
        <div ref={sheetRef} id="pop-report-sheet" style={{ width: 720, maxWidth: '100%', margin: '0 auto', background: THEME.sheetBg, borderRadius: 16, boxShadow: '0 30px 80px rgba(0,0,0,0.55)', padding: 26, display: 'flex', flexDirection: 'column', gap: 20, fontSize: 12, color: THEME.text }}>

          {/* Header: wordmark */}
          <div>
            <img src="/logo-light.svg" alt="Smartstreet" style={{ height: 46 }} />
            <div style={{ height: 1, background: '#E5E9EC', marginTop: 16 }} />
          </div>

          {/* Campaign title + hero impressions figure, hero thumbnail alongside both.
              alignItems: 'stretch' lets the left column match the (taller) hero
              image's height, and the impressions figure below is flex: 1 so it
              grows to fill whatever gap that leaves between the title block and
              the stat row, rather than leaving dead space at the bottom. */}
          <div style={{ display: 'grid', gridTemplateColumns: heroImageUrl ? '1fr 190px' : '1fr', gap: 20, alignItems: 'stretch' }}>
            <div style={{ paddingTop: 4, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: THEME.textMuted, marginBottom: 6 }}>Proof of Play</div>
                <div style={{
                  fontSize: 26, fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.01em', color: THEME.textStrong,
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'break-word',
                }}>
                  {campaign.name}
                </div>
                {subtitle && <div style={{ fontSize: 11.5, color: THEME.textMuted, marginTop: 6 }}>{subtitle}</div>}
              </div>

              <div style={{ flex: 1, display: 'flex', alignItems: 'center', minHeight: 14, width: '100%' }}>
                {/* Stacked, not side-by-side, so the "Impressions" label always
                    lands on its own line under the figure regardless of how
                    wide the number is. */}
                <div style={{ width: '100%' }}>
                  <div style={{ fontSize: 84, fontWeight: 800, lineHeight: 1, letterSpacing: '-0.02em', color: THEME.green, fontVariantNumeric: 'tabular-nums', width: '100%' }}>
                    {fmtFull(totals.impressions)}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: THEME.textStrong }}>Impressions</div>
                    <div style={{ fontSize: 11, color: THEME.textMuted, marginTop: 2 }}>verified audience delivered across the flight</div>
                  </div>
                </div>
              </div>
            </div>

            {heroImageUrl && (
              stackImageUrls.length === 1 ? (
                <img
                  src={heroImageUrl}
                  crossOrigin="anonymous"
                  alt="Featured creative"
                  style={{ width: 150, height: 267, flexShrink: 0, alignSelf: 'start', objectFit: 'cover', borderRadius: 14, border: THEME.panelBorder, boxShadow: THEME.panelShadow, display: 'block' }}
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                />
              ) : (
                // Side by side, top performer first — the container is
                // deliberately narrower than the full row when there's a
                // third creative, clipping it to a visible-but-partial
                // sliver as a "there's more" cue, rather than hiding it
                // behind the others.
                <div style={{
                  width: stackImageUrls.length === 2 ? THUMB_W * 2 + THUMB_GAP : THUMB_W * 2 + THUMB_GAP * 2 + THUMB_W / 2,
                  height: THUMB_H, overflow: 'hidden', flexShrink: 0, alignSelf: 'start',
                }}>
                  <div style={{ display: 'flex', gap: THUMB_GAP }}>
                    {stackImageUrls.map((url, i) => (
                      <img
                        key={i}
                        src={url}
                        crossOrigin="anonymous"
                        alt={i === 0 ? 'Featured creative' : 'Additional creative'}
                        style={{ width: THUMB_W, height: THUMB_H, flexShrink: 0, objectFit: 'cover', borderRadius: 10, border: THEME.panelBorder, display: 'block' }}
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                      />
                    ))}
                  </div>
                </div>
              )
            )}
          </div>

          {/* Stat row */}
          <div style={{ display: 'flex', alignItems: 'stretch', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            {[
              { value: fmtFull(totals.plays), label: 'Plays' },
              { value: avgImprPerPlay != null ? avgImprPerPlay.toFixed(1) : '—', label: 'Average Impressions per Play' },
              { value: fmtFull(screens.length), label: 'Screens' },
              { value: fmtFull(uniqueLocations), label: 'Locations' },
            ].map((s, i) => (
              <React.Fragment key={s.label}>
                {i > 0 && <div style={{ width: 1, background: '#E6E9ED', alignSelf: 'stretch' }} />}
                <Stat value={s.value} label={s.label} />
              </React.Fragment>
            ))}
          </div>

          {/* Average Delivery, Hour by Hour, + where it ran */}
          <div>
            <div style={{ fontSize: 14.5, fontWeight: 800, color: THEME.textStrong, marginBottom: 10 }}>Average Delivery, Hour by Hour</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: 12 }}>
              <div style={{ ...panelStyle, display: 'flex', flexDirection: 'column' }}>
                {hourlyData.some(h => h.impressions > 0) ? (
                  <HourlyBarChart data={hourlyData} />
                ) : (
                  <div style={{ fontSize: 11, color: THEME.textFaint, padding: '20px 0' }}>Not enough hourly delivery data collected yet for this flight.</div>
                )}
              </div>
              {showMap ? (
                <NetworkMap
                  screens={campaignPins}
                  width={200}
                  height={280}
                  dot={12}
                  pad={22}
                  fill
                  minHeight={280}
                  showNote={false}
                  onFail={() => setMapFailed(true)}
                  style={{ border: THEME.panelBorder }}
                />
              ) : (
                <div style={{
                  width: '100%', height: '100%', minHeight: 280, borderRadius: 12,
                  border: `1px dashed #C7CDD4`, background: THEME.trackBg,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
                }}>
                  <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: THEME.textFaint }}>Map</span>
                  <span style={{ fontSize: 8.5, color: THEME.textFaint, textAlign: 'center', padding: '0 10px' }}>Screen locations unavailable</span>
                </div>
              )}
            </div>

            {hourlyData.some(h => h.impressions > 0) && (peakHour || avgImpressionsPerScreenPerDay != null) && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                {peakHour && overallHourlyAverage > 0 && (
                  <InsightChip value={`${(peakHour.impressions / overallHourlyAverage).toFixed(1)}x`} text={<>The {hourLabelFull(peakHour.hour)} hour averages <strong style={{ color: THEME.textStrong }}>{fmtFull(Math.round(peakHour.impressions))} impressions</strong> a day, {(peakHour.impressions / overallHourlyAverage).toFixed(1)}x the hourly average.</>} />
                )}
                {avgImpressionsPerScreenPerDay != null && (
                  <InsightChip value={fmtNum(Math.round(avgImpressionsPerScreenPerDay))} text={<>Each screen delivered an average of <strong style={{ color: THEME.textStrong }}>{fmtFull(Math.round(avgImpressionsPerScreenPerDay))} impressions</strong> per day.</>} />
                )}
              </div>
            )}
          </div>

          {/* Top screens + demographics — both cards stretch to the row's full
              height (whichever side is naturally taller), so they read as a
              matched pair rather than the demographics card looking taller
              just because it has more content. */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 22, alignItems: 'stretch' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={sectionLabelStyle}>Top Screens - Share of Total {fmtNum(totals.impressions)} Impressions</div>
              <div style={{ ...panelStyle, marginTop: 10, padding: 14, flex: 1 }}>
                {topScreens.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {topScreens.map(s => {
                      const impressions = s.impressions ?? 0;
                      const pct = (impressions / maxScreenImpressions) * 100;
                      return (
                        <div key={s.id}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
                            <span style={{ fontWeight: 600, color: THEME.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }}>{s.venue || s.name}</span>
                            <span style={{ fontWeight: 800, color: THEME.greenDeep, flexShrink: 0 }}>{fmtNum(impressions)}</span>
                          </div>
                          <div style={{ height: 6, background: THEME.trackBg, borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ width: `${Math.max(pct, 3)}%`, height: '100%', background: THEME.green, borderRadius: 3 }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ fontSize: 11, color: THEME.textFaint }}>No screen data yet</div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={sectionLabelStyle}>Who Was in Front of the Screens</div>
              <div style={{ ...panelStyle, marginTop: 10, padding: 14, flex: 1 }}>
                {demographics ? (
                  <>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {demographics.gender.map((g, i) => (
                        <div key={g.label} style={{ flex: 1, background: i % 2 === 0 ? THEME.greenDark : THEME.navy, borderRadius: 10, padding: '10px 12px' }}>
                          <div style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>{g.share}%</div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.85)', marginTop: 1 }}>{g.label}</div>
                        </div>
                      ))}
                    </div>
                    {leadGender && <div style={{ fontSize: 9.5, color: THEME.textMuted, marginTop: 8 }}>Audience skewed {leadGender.label.toLowerCase()} ({leadGender.share}%).</div>}

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 12 }}>
                      {demographics.age.map((a, i) => (
                        <div key={a.band} style={{ display: 'grid', gridTemplateColumns: '40px 1fr 34px', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 9.5, color: THEME.textMuted }}>{a.band}</span>
                          <div style={{ height: 9, background: THEME.trackBg, borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{ width: `${Math.max((a.share / maxAgeShare) * 100, 3)}%`, height: '100%', background: i < demographics.age.length / 2 ? THEME.green : THEME.navy, borderRadius: 4 }} />
                          </div>
                          <span style={{ fontSize: 9.5, fontWeight: 700, textAlign: 'right', color: THEME.textStrong }}>{a.share}%</span>
                        </div>
                      ))}
                    </div>
                    {leadAge && <div style={{ fontSize: 9.5, color: THEME.textMuted, marginTop: 8 }}>Led by the {leadAge.band} age band ({leadAge.share}%).</div>}
                  </>
                ) : (
                  <div style={{ fontSize: 11, color: THEME.textFaint }}>Not enough sensor data collected yet for this flight.</div>
                )}
              </div>
            </div>
          </div>

        </div>
        )}
      </div>
  );

  return createPortal(content, document.body);
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <div style={{ fontSize: 26, fontWeight: 800, color: THEME.textStrong, lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: THEME.textMuted, marginTop: 3 }}>{label}</div>
    </div>
  );
}

function InsightChip({ value, text }: { value: string; text: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: THEME.panelBg, border: THEME.panelBorder, borderLeft: `4px solid ${THEME.green}`, borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: THEME.green, flexShrink: 0 }}>{value}</div>
      <div style={{ fontSize: 10.5, color: THEME.text, lineHeight: 1.4 }}>{text}</div>
    </div>
  );
}

function fmtNum(n: number): string {
  if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (Math.abs(n) >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return n.toLocaleString('en-US');
}

const sectionLabelStyle: React.CSSProperties = { fontSize: 9.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: THEME.textMuted };
const panelStyle: React.CSSProperties = { background: THEME.panelBg, border: THEME.panelBorder, borderRadius: 12, padding: '14px 16px', boxShadow: THEME.panelShadow };
